import React, { useEffect, useRef, useState } from "react";
import { api } from "../api";
import StoryDashboardImageSettings from "./StoryDashboardImageSettings";

const VISIBILITY_OPTIONS = [
  "private_draft",
  "nextscenes_readers",
  "public_preview",
  "published",
];

function safeText(value) {
  return String(value || "").trim();
}

function normalizeVisibility(value, publicationStatus = "") {
  const raw = safeText(value).toLowerCase().replace(/[\s-]+/g, "_");
  const publication = safeText(publicationStatus).toLowerCase();

  if (
    publication === "publishedamazon" ||
    publication === "publishedexternal"
  ) {
    return "published";
  }

  if (raw === "private" || raw === "private_draft" || raw === "hidden") {
    return "private_draft";
  }

  if (
    raw === "nextscenes_readers" ||
    raw === "readers" ||
    raw === "readers_only"
  ) {
    return "nextscenes_readers";
  }

  if (raw === "published") return "published";
  return "public_preview";
}

function visibilityLegacyValue(status) {
  if (status === "private_draft") return "private";
  if (status === "nextscenes_readers") return "readers";
  return "public";
}

function visibilityLabel(status, isFR) {
  const labels = {
    private_draft: {
      en: "Private Draft",
      fr: "Brouillon privé",
    },
    nextscenes_readers: {
      en: "NextScenes Readers Only",
      fr: "Lecteurs NextScenes seulement",
    },
    public_preview: {
      en: "Public Preview",
      fr: "Aperçu public",
    },
    published: {
      en: "Published / Completed",
      fr: "Publié / terminé",
    },
  };

  return labels[status]?.[isFR ? "fr" : "en"] || labels.public_preview.en;
}

function visibilityHelp(status, isFR) {
  const help = {
    private_draft: {
      en: "Only the author and Admin can see this storyline.",
      fr: "Seuls l’auteur et Admin peuvent voir cette storyline.",
    },
    nextscenes_readers: {
      en: "Logged-in NextScenes Readers can read it; guests cannot.",
      fr: "Les Readers connectés peuvent la lire, mais pas les invités.",
    },
    public_preview: {
      en: "Guests and logged-in Readers can read the approved public preview.",
      fr: "Les invités et Readers connectés peuvent lire l’aperçu public approuvé.",
    },
    published: {
      en: "Public completed-book visibility for finished or published work.",
      fr: "Visibilité publique pour une œuvre terminée ou publiée.",
    },
  };

  return help[status]?.[isFR ? "fr" : "en"] || "";
}

function initialForm(story) {
  const publicationStatus =
    safeText(story?.publicationStatus || story?.publishingStatus) ||
    "inDevelopment";

  return {
    title: safeText(story?.title),
    description: safeText(story?.description),
    synopsis: safeText(story?.synopsis),
    aboutAuthor: safeText(story?.aboutAuthor),

    visibilityStatus: normalizeVisibility(
      story?.visibilityStatus ||
        story?.readerVisibility ||
        story?.storyVisibility ||
        story?.visibility,
      publicationStatus
    ),

    publicationStatus,
    publicationTitle: safeText(
      story?.publicationTitle || story?.publishedTitle || story?.title
    ),
    publicationSubtitle: safeText(
      story?.publicationSubtitle || story?.publishedSubtitle
    ),
    publishedAuthorName: safeText(
      story?.publishedAuthorName ||
        story?.publicationAuthorName ||
        story?.authorName ||
        story?.authorProfile?.name
    ),
    publicationUrl: safeText(
      story?.publicationUrl ||
        story?.publishedUrl ||
        story?.amazonUrl ||
        story?.amazonPageUrl
    ),
    publicationCoverUrl: safeText(
      story?.publicationCoverUrl ||
        story?.publishedCoverUrl ||
        story?.finalCoverImageUrl ||
        story?.coverImageUrl
    ),
    publishedAt: safeText(story?.publishedAt),
    publicationNote: safeText(story?.publicationNote),
  };
}

export default function StoryDashboardOwnerSettings({
  open,
  story,
  storyId,
  lang = "en",
  onClose,
  onSaved,
}) {
  const isFR = String(lang).toLowerCase().startsWith("fr");
  const [form, setForm] = useState(() => initialForm(story));
  const [section, setSection] = useState("details");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const lastOpenedStoryIdRef = useRef("");

  useEffect(() => {
    if (!open) {
      lastOpenedStoryIdRef.current = "";
      return;
    }

    const currentStoryId = safeText(
      storyId || story?._id || story?.id
    );

    if (lastOpenedStoryIdRef.current === currentStoryId) return;

    lastOpenedStoryIdRef.current = currentStoryId;
    setForm(initialForm(story));
    setSection("details");
    setMessage("");
  }, [open, story, storyId]);

  if (!open) return null;

  function updateField(name, value) {
    setMessage("");

    setForm((current) => {
      const next = { ...current, [name]: value };

      if (name === "publicationStatus") {
        if (
          value === "publishedAmazon" ||
          value === "publishedExternal"
        ) {
          next.visibilityStatus = "published";
        } else if (current.visibilityStatus === "published") {
          next.visibilityStatus = "public_preview";
        }
      }

      return next;
    });
  }

  async function saveSettings() {
    if (!storyId || busy) return;

    try {
      setBusy(true);
      setMessage("");

      const visibilityStatus = normalizeVisibility(
        form.visibilityStatus,
        form.publicationStatus
      );

      const payload = {
        title: safeText(form.title),
        description: safeText(form.description),
        synopsis: safeText(form.synopsis),
        aboutAuthor: safeText(form.aboutAuthor),

        visibilityStatus,
        visibility: visibilityLegacyValue(visibilityStatus),

        publicationStatus: safeText(form.publicationStatus),
        publicationTitle: safeText(form.publicationTitle),
        publicationSubtitle: safeText(form.publicationSubtitle),
        publishedAuthorName: safeText(form.publishedAuthorName),
        publicationUrl: safeText(form.publicationUrl),
        publicationCoverUrl: safeText(form.publicationCoverUrl),
        publishedAt: safeText(form.publishedAt),
        publicationNote: safeText(form.publicationNote),
      };

      const saved = await api.put(
        `/api/stories/${encodeURIComponent(storyId)}`,
        payload
      );

      if (typeof onSaved === "function") {
        await onSaved(saved);
      }

      setMessage(
        isFR
          ? "Paramètres de la storyline enregistrés ✓"
          : "Story settings saved ✓"
      );
    } catch (error) {
      setMessage(
        safeText(error?.message) ||
          (isFR
            ? "Impossible d’enregistrer les paramètres."
            : "Could not save story settings.")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="ns-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={
        isFR ? "Paramètres de la storyline" : "Story Settings"
      }
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
        background: "rgba(15, 23, 42, 0.58)",
      }}
    >
      <div
        className="ns-card ns-dashboard-modal"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(920px, 100%)",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 20,
        }}
      >
        <div className="ns-modal-header">
          <div>
            <div className="ns-modal-kicker">
              {isFR ? "Autorité de l’auteur" : "Author authority"}
            </div>
            <h2 style={{ margin: "4px 0 0" }}>
              {isFR ? "Paramètres de la storyline" : "Story Settings"}
            </h2>
          </div>

          <button
            type="button"
            className="ns-button ns-button-outline"
            onClick={onClose}
          >
            {isFR ? "Fermer" : "Close"}
          </button>
        </div>

        <p className="ns-card-meta">
          {isFR
            ? "Gérez les détails, les images, la visibilité et l’identité officielle de publication de cette storyline."
            : "Manage this storyline’s details, images, visibility, and official publication identity."}
        </p>

        <div
          className="ns-dashboard-actions"
          style={{ margin: "16px 0" }}
        >
          <button
            type="button"
            className={
              section === "details"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            onClick={() => setSection("details")}
          >
            {isFR ? "Détails" : "Story details"}
          </button>

          <button
            type="button"
            className={
              section === "images"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            onClick={() => setSection("images")}
          >
            {isFR ? "Images" : "Images"}
          </button>

          <button
            type="button"
            className={
              section === "visibility"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            onClick={() => setSection("visibility")}
          >
            {isFR ? "Visibilité" : "Visibility"}
          </button>

          <button
            type="button"
            className={
              section === "publication"
                ? "ns-button ns-button-primary"
                : "ns-button ns-button-outline"
            }
            onClick={() => setSection("publication")}
          >
            {isFR ? "Publication" : "Publication"}
          </button>
        </div>

        {section === "details" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <strong>{isFR ? "Titre" : "Title"}</strong>
              <input
                className="ns-input"
                value={form.title}
                maxLength={240}
                onChange={(event) =>
                  updateField("title", event.target.value)
                }
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <strong>{isFR ? "Description" : "Description"}</strong>
              <textarea
                className="ns-input"
                value={form.description}
                rows={5}
                onChange={(event) =>
                  updateField("description", event.target.value)
                }
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <strong>{isFR ? "Synopsis" : "Synopsis"}</strong>
              <textarea
                className="ns-input"
                value={form.synopsis}
                rows={5}
                onChange={(event) =>
                  updateField("synopsis", event.target.value)
                }
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              <strong>
                {isFR ? "À propos de l’auteur" : "About the Author"}
              </strong>
              <textarea
                className="ns-input"
                value={form.aboutAuthor}
                rows={5}
                onChange={(event) =>
                  updateField("aboutAuthor", event.target.value)
                }
              />
            </label>
          </div>
        ) : null}

        {section === "images" ? (
          <StoryDashboardImageSettings
            story={story}
            storyId={storyId}
            lang={lang}
            onSaved={onSaved}
          />
        ) : null}

        {section === "visibility" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <strong>
                {isFR ? "Qui peut lire ?" : "Who can read?"}
              </strong>

              <select
                className="ns-input"
                value={form.visibilityStatus}
                disabled={
                  form.publicationStatus === "publishedAmazon" ||
                  form.publicationStatus === "publishedExternal"
                }
                onChange={(event) =>
                  updateField("visibilityStatus", event.target.value)
                }
              >
                {VISIBILITY_OPTIONS.map((status) => (
                  <option
                    key={status}
                    value={status}
                    disabled={
                      status === "published" &&
                      form.publicationStatus !== "publishedAmazon" &&
                      form.publicationStatus !== "publishedExternal"
                    }
                  >
                    {visibilityLabel(status, isFR)}
                  </option>
                ))}
              </select>
            </label>

            <p className="ns-card-meta" style={{ margin: 0 }}>
              {visibilityHelp(form.visibilityStatus, isFR)}
            </p>

            {(form.publicationStatus === "publishedAmazon" ||
              form.publicationStatus === "publishedExternal") && (
              <p className="ns-card-meta" role="note">
                {isFR
                  ? "Une œuvre publiée utilise automatiquement la visibilité Publié / terminé."
                  : "A published work automatically uses Published / Completed visibility."}
              </p>
            )}
          </div>
        ) : null}

        {section === "publication" ? (
          <div
            className="ns-publication-form-grid"
            style={{ display: "grid", gap: 14 }}
          >
            <label>
              <span>{isFR ? "Statut" : "Publication status"}</span>
              <select
                className="ns-input"
                value={form.publicationStatus}
                onChange={(event) =>
                  updateField("publicationStatus", event.target.value)
                }
              >
                <option value="inDevelopment">
                  {isFR ? "En développement" : "In development"}
                </option>
                <option value="completed">
                  {isFR ? "Terminé / peaufiné" : "Completed / polished"}
                </option>
                <option value="publishedAmazon">
                  {isFR ? "Publié sur Amazon" : "Published on Amazon"}
                </option>
                <option value="publishedExternal">
                  {isFR ? "Publié ailleurs" : "Published elsewhere"}
                </option>
              </select>
            </label>

            <label>
              <span>{isFR ? "Titre publié" : "Published title"}</span>
              <input
                className="ns-input"
                value={form.publicationTitle}
                maxLength={160}
                onChange={(event) =>
                  updateField("publicationTitle", event.target.value)
                }
              />
            </label>

            <label>
              <span>
                {isFR ? "Sous-titre publié" : "Published subtitle"}
              </span>
              <input
                className="ns-input"
                value={form.publicationSubtitle}
                maxLength={240}
                placeholder={isFR ? "Facultatif" : "Optional"}
                onChange={(event) =>
                  updateField("publicationSubtitle", event.target.value)
                }
              />
            </label>

            <label>
              <span>
                {isFR
                  ? "Nom d’auteur publié"
                  : "Published author name"}
              </span>
              <input
                className="ns-input"
                value={form.publishedAuthorName}
                maxLength={160}
                placeholder={
                  isFR ? "Exemple : Goddy Oguzie" : "Example: Goddy Oguzie"
                }
                onChange={(event) =>
                  updateField("publishedAuthorName", event.target.value)
                }
              />
            </label>

            <label>
              <span>
                {isFR
                  ? "Lien Amazon / publication"
                  : "Amazon / publication link"}
              </span>
              <input
                className="ns-input"
                value={form.publicationUrl}
                placeholder="https://..."
                onChange={(event) =>
                  updateField("publicationUrl", event.target.value)
                }
              />
            </label>

            <label>
              <span>
                {isFR
                  ? "URL de la couverture publiée"
                  : "Published cover URL"}
              </span>
              <input
                className="ns-input"
                value={form.publicationCoverUrl}
                placeholder="https://..."
                onChange={(event) =>
                  updateField("publicationCoverUrl", event.target.value)
                }
              />
            </label>

            <label>
              <span>
                {isFR ? "Date de publication" : "Publication date"}
              </span>
              <input
                className="ns-input"
                value={form.publishedAt}
                placeholder="YYYY-MM-DD"
                onChange={(event) =>
                  updateField("publishedAt", event.target.value)
                }
              />
            </label>

            <label>
              <span>
                {isFR ? "Note de publication" : "Publication note"}
              </span>
              <textarea
                className="ns-input"
                value={form.publicationNote}
                rows={4}
                placeholder={isFR ? "Facultatif" : "Optional"}
                onChange={(event) =>
                  updateField("publicationNote", event.target.value)
                }
              />
            </label>
          </div>
        ) : null}

        {message ? (
          <p className="ns-card-meta" role="status" style={{ marginTop: 16 }}>
            {message}
          </p>
        ) : null}

        <div
          className="ns-modal-actions"
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginTop: 18,
          }}
        >
          <button
            type="button"
            className="ns-button ns-button-primary"
            disabled={busy || !storyId}
            onClick={saveSettings}
          >
            {busy
              ? isFR
                ? "Enregistrement…"
                : "Saving…"
              : isFR
                ? "Enregistrer les paramètres"
                : "Save Story Settings"}
          </button>

          <button
            type="button"
            className="ns-button ns-button-outline"
            disabled={busy}
            onClick={onClose}
          >
            {isFR ? "Annuler" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}
